import { useEffect, useReducer } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';

/**
 * Re-renders the calling component whenever the element matching `selector`
 * changes size. Use it when a layout value is measured live during render
 * rather than held in state, so the render stays in sync with the element's
 * box even when nothing else would trigger an update.
 *
 * The element is looked up once, when the effect runs, so it has to already be
 * in the document — true for server-rendered page-template markup.
 *
 * @param {string} selector - querySelector for the element to observe.
 * @param {boolean} [enabled] - skip observing when false.
 * @param {number} [throttleMs] - minimum gap between re-renders.
 */
export const useResizeObserver = (
	selector,
	enabled = true,
	throttleMs = 100
) => {
	const [, forceRender] = useReducer((tick) => tick + 1, 0);

	useEffect(() => {
		if (!isBrowser || !enabled) return undefined;
		if (typeof ResizeObserver === 'undefined') return undefined;

		const element = document.querySelector(selector);
		if (!element) return undefined;

		// An animating element can resize every frame, so throttle to keep that
		// from becoming a re-render — and a layout read — per frame. Leading and
		// trailing edges both fire, so the first change shows up immediately and
		// the final resting size is never missed.
		const onResize = window._.throttle(forceRender, throttleMs);
		const observer = new ResizeObserver(onResize);
		observer.observe(element);
		return () => {
			observer.disconnect();
			onResize.cancel?.();
		};
	}, [selector, enabled, throttleMs]); // eslint-disable-line react-hooks/exhaustive-deps
};
